/**
 * What to call a friend on the schedule.
 *
 * A friend's annotation on the day view carries a name prefix and the user's own
 * carries none (R13), so this string is the only thing distinguishing "someone else
 * wants to see this" from "I do". It has to be short enough to sit in front of a note
 * on a phone row and stable enough that the same person reads the same way every time.
 *
 * The address is the first source because it is the one the user chose when they added
 * the friend, and because it is the one the annotation document has no idea about —
 * `ownerName` there is a Google display name, which is why the friend list carries
 * addresses at all (KTD6). Display name is the fallback, and both fall back to the
 * label the rest of the app already uses for a person it cannot name.
 */

/**
 * The label for a friend with neither an address nor a name on file.
 *
 * Same string `AuthProvider` stamps on an account Google gives no display name for,
 * and the same one the server writes into a friend list entry. Deliberately a name
 * shaped like a person rather than "Ukjent": it is rendered as `En venn: …` in front
 * of a note, where a technical placeholder would read as a bug.
 */
export const FRIEND_FALLBACK_PREFIX = 'En venn'

/** Whatever is known about a friend's identity. Both fields are routinely absent. */
export interface FriendIdentity {
  email?: string | null
  name?: string | null
}

/**
 * The first name component of a string, capitalized — or `''` when there is none.
 *
 * The cut at the first dot is what turns a login into a name: `thomas.oldervoll`
 * is one person's address, `Thomas` is what their friends call them. Case is folded
 * first because an address carries none — it is case-insensitive by definition, and
 * `TOM.X@example.com` is the same person as `tom.x@example.com`.
 */
function firstNameComponent(value: string): string {
  const cut = value.split('.')[0]?.trim().toLowerCase() ?? ''
  if (cut === '') return ''
  return cut.charAt(0).toUpperCase() + cut.slice(1)
}

/**
 * The prefix a friend's annotation is rendered with (R14).
 *
 * Takes the address's local part up to its first dot, or — with no address — the first
 * word of the display name, treated the same way. A friendship proposed from an
 * emailed share exists before the other party has ever signed in, so "no address yet"
 * and "no name yet" are both ordinary states rather than corrupt records, and all
 * three levels of this fallback are reachable in the shipped app.
 */
export function friendPrefix({ email, name }: FriendIdentity): string {
  const localPart = (email ?? '').split('@')[0] ?? ''
  const fromEmail = firstNameComponent(localPart)
  if (fromEmail !== '') return fromEmail

  const firstWord = (name ?? '').trim().split(/\s+/)[0] ?? ''
  const fromName = firstNameComponent(firstWord)
  if (fromName !== '') return fromName

  return FRIEND_FALLBACK_PREFIX
}
